/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { getAtpItemFields, type AtpStatus } from './atp-fields';

/**
 * Which delivery message to render for a line item. Mirrors the legacy
 * `org_ashcomm` Delivery.js Get-It-By / Ships-by / unavailable branches, but
 * trusts the per-item flags the cartridge already computed server-side rather
 * than re-deriving the threshold logic on the client.
 */
export type AtpDeliveryVariant = 'getItBy' | 'shipsBy' | 'backordered' | 'unavailable';

/**
 * A locale-agnostic description of the delivery message for one line item.
 * The component layer formats the dates and resolves the i18n string — this
 * stays pure so it can be unit-tested without React or i18n.
 */
export interface AtpDeliveryDescriptor {
    variant: AtpDeliveryVariant;
    /** Promised date (ISO `YYYY-MM-DD`) for `getItBy` / `shipsBy`. */
    date: string | null;
    /** Backorder window start (ISO `YYYY-MM-DD`) for `backordered`. */
    rangeStart: string | null;
    /** Backorder window end (ISO `YYYY-MM-DD`) for `backordered`. */
    rangeEnd: string | null;
}

/**
 * Resolve the delivery message for a single basket line item.
 *
 * The decision is driven entirely by the per-item `c_atp*` fields. The
 * enrichment hook only writes those fields when the basket was ATP-enriched
 * (it returns early without touching items on `no_zip`/`error`), so an
 * un-enriched item naturally yields `null` here without needing the basket
 * status. `basketStatus`, when supplied, is treated as an explicit gate:
 * anything other than `'enriched'` suppresses the message regardless of the
 * item fields (useful for tests/stories or a caller that wants to hard-hide).
 *
 * Returns `null` when nothing should render:
 *  - `basketStatus` is supplied and is not `'enriched'`;
 *  - the item is third-party-fulfilled (3PBB) with no ATP visibility;
 *  - ATP returned no usable date and no explicit unavailability.
 *
 * @param item - The SCAPI basket product item carrying `c_atp*` fields.
 * @param basketStatus - Optional basket-level `c_atpStatus` gate.
 */
export function resolveAtpDeliveryMessage(
    item: unknown,
    basketStatus?: AtpStatus | null
): AtpDeliveryDescriptor | null {
    if (basketStatus !== undefined && basketStatus !== 'enriched') {
        return null;
    }

    const fields = getAtpItemFields(item);

    // 3PBB items ship from a partner warehouse ATP can't see — availability is
    // intentionally left null, so we render no estimate rather than guessing.
    if (fields.is3PBB && fields.isAvailable === null) {
        return null;
    }

    if (fields.isAvailable === false) {
        return { variant: 'unavailable', date: null, rangeStart: null, rangeEnd: null };
    }

    if (fields.isOnBackOrder) {
        return {
            variant: 'backordered',
            date: null,
            rangeStart: fields.beginDateRange,
            rangeEnd: fields.endDateRange,
        };
    }

    if (fields.bestDate) {
        return {
            variant: fields.isGetItBy ? 'getItBy' : 'shipsBy',
            date: fields.bestDate,
            rangeStart: null,
            rangeEnd: null,
        };
    }

    return null;
}
